import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchProjectProfileInfo = createAsyncThunk(
  'projectProfile/fetchInfo',
  async ({ projectNumber }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/projectProfile/projectInfo`, {
        signal,
        params: { projectNumber },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchInfo failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch project info');
    }
  }
);

export const fetchProjectProfileTimesheets = createAsyncThunk(
  'projectProfile/fetchTimesheets',
  async ({ projectNumber, mode }, { signal, rejectWithValue }) => {
    try {
      // Simplify and normalize mode for the API, while preserving the original label.
      const rawMode = (mode || '').toString().trim();
      let normalizedMode = rawMode;
      if (rawMode.toUpperCase() === 'SUMMARY - PHASE') {
        normalizedMode = 'SUMMARY_PHASE';
      } else if (rawMode.toUpperCase() === 'SUMMARY - PERSON') {
        normalizedMode = 'SUMMARY_PERSON';
      }

      // Flask endpoints are sometimes inconsistent about query param naming across deployments.
      // Send a few common aliases to maximize compatibility without requiring backend changes.
      const res = await axios.get(`${API_URL}/api/timesheets/view`, {
        signal,
        params: {
          Mode: normalizedMode,
          mode: normalizedMode,
          ModeLabel: rawMode,
          projectNumber,
          ProjectNumber: projectNumber,
          jobno: projectNumber,
          JobNo: projectNumber,
        },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchTimesheets failed', e);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to fetch timesheets';
      return rejectWithValue(msg);
    }
  }
);

export const fetchProjectProfileLogBook = createAsyncThunk(
  'projectProfile/fetchLogBook',
  async ({ projectNumber }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/projectProfile/projectLogBook`, {
        signal,
        params: { projectNumber },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchLogBook failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch project log book');
    }
  }
);

export const fetchProjectProfileFilters = createAsyncThunk(
  'projectProfile/fetchFilters',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/projectProfile/filters`, { signal });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchFilters failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch project filters');
    }
  }
);

export const fetchOpportunityLostReasons = createAsyncThunk(
  'projectProfile/fetchLostReasons',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/bid/opportunity/lost/reason`, { signal });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchLostReasons failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch lost reasons');
    }
  }
);

export const fetchProjectBillPayers = createAsyncThunk(
  'projectProfile/fetchBillPayers',
  async ({ projectNumber }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoice/project/bill/payer/${projectNumber}`, { signal });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchBillPayers failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch bill payers');
    }
  }
);

export const fetchProjectPhases = createAsyncThunk(
  'projectProfile/fetchPhases',
  async ({ projectNumber }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoice/project/phases/${projectNumber}`, { signal });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchPhases failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch phases');
    }
  }
);

export const fetchProjectPlannedInvoices = createAsyncThunk(
  'projectProfile/fetchPlannedInvoices',
  async ({ projectNumber }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoice/project/planned/invoice/${projectNumber}`, { signal });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/fetchPlannedInvoices failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch planned invoices');
    }
  }
);

export const postProjectUpdateInfo = createAsyncThunk(
  'projectProfile/postUpdateInfo',
  async ({ projectNumber, userEmail, sectionName, updatedValues }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/updateProjectInfo`,
        {
          UserEmail: userEmail,
          SectionName: sectionName,
          UpdatedValues: updatedValues,
        },
        {
          signal,
          params: { projectNumber },
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/postUpdateInfo failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to update project');
    }
  }
);

export const postProjectComment = createAsyncThunk(
  'projectProfile/postComment',
  async ({ jobno, status, activity, comment, email }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/submitProjectComment`,
        { jobno, status, activity, comment, email },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('projectProfile/postComment failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to submit comment');
    }
  }
);

const initialState = {
  error: null,
};

const projectProfileSlice = createSlice({
  name: 'projectProfile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('projectProfile/') && action.type.endsWith('/rejected'),
      (state, action) => {
        if (action?.meta?.aborted) return;
        state.error = action.payload || action.error?.message || null;
      }
    );
  },
});

export default projectProfileSlice.reducer;


